import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { Icon } from '@tcc/crate';
import { getMessage } from '@tcc/i18n';
import styles from './styles.css';

export type FilesViewerProps = {
  urls: string[];
  initialIndex?: number;
  onEdit?: (editIndex: number) => void;
  onNavigation?: (index: number) => void;
};

export const FilesViewer = ({ urls, initialIndex = 0, onEdit, onNavigation }: FilesViewerProps) => {
  const [currentUrlIndex, setCurrentUrlIndex] = useState<number>(initialIndex);
  const amountOfUrls = urls.length;

  useEffect(() => {
    onNavigation?.(currentUrlIndex);
  }, [currentUrlIndex, onNavigation]);

  if (!amountOfUrls) return null;

  const hasMoreThanOnePreview = amountOfUrls > 1;
  const showPrevArrow = hasMoreThanOnePreview && currentUrlIndex > 0;
  const showNextArrow = hasMoreThanOnePreview && currentUrlIndex < (amountOfUrls - 1);
  const showNavigation = showPrevArrow || showNextArrow;
  const photosAmountLabel = getMessage('enhancedScouting.form.viewer.currentPhoto', {
    currentPhotoNumber: currentUrlIndex + 1,
    totalAmountOfPhotos: amountOfUrls,
  });

  return <div className={styles.viewerContainer} data-test-id="enhanced-scouting-files-viewer-container">
    <div className={styles.imageContainer} data-test-id="enhanced-scouting-image-container">
      {urls.map((url, i) => (
        <img
          key={url}
          data-test-id={currentUrlIndex === i ? 'enhanced-scouting-current-image' : `enhanced-scouting-image-${i}`}
          alt={`enhanced-scouting-${i}`} 
          className={styles.previewImg}
          style={{ ...(currentUrlIndex !== i ? ({ display: 'none' }) : {}) }}
          src={url} />
        )
      )}
    </div>
    <div
      className={classNames(styles.viewerOverlayUi, styles.viewerEditAndAmountContainer)}
      data-test-id="enhanced-scouting-files-viewer-edit-container">
      {onEdit && <div
        onClick={() => onEdit(currentUrlIndex)}
        className={classNames(styles.viewerSelectableElement, styles.viewerUIElement, styles.viewerEdit)}
        data-test-id="enhanced-scouting-files-viewer-edit-pencil-container">
        <Icon glyph={Icon.set.PENCIL} testId="enhanced-scouting-files-viewer-edit-pencil" />
      </div>}
      <div 
        className={classNames(styles.viewerUIElement, !onEdit ? styles.viewerRightUIElement : undefined)}
        data-test-id="enhanced-scouting-files-viewer-images-label"
      >
        {photosAmountLabel}
      </div>
    </div>
    {showNavigation && 
      <div
        className={classNames(styles.viewerOverlayUi, styles.viewerNavigationContainer)}
        data-test-id="enhanced-scouting-files-viewer-navigation-container">
        <div
          className={showPrevArrow ? classNames(styles.viewerSelectableElement, styles.viewerUIElement, styles.viewerNavIcon) : ''}
          onClick={() => setCurrentUrlIndex(prevVal => prevVal - 1)}
          data-test-id="enhanced-scouting-files-viewer-prev-arrow-container"
        >
          {showPrevArrow && <Icon glyph={Icon.set.ARROW_LEFT} testId="enhanced-scouting-files-viewer-prev-arrow" />}
        </div>
        <div
          className={showNextArrow ? classNames(styles.viewerSelectableElement, styles.viewerUIElement, styles.viewerNavIcon) : ''}
          onClick={() => setCurrentUrlIndex(prevVal => prevVal + 1)}
          data-test-id="enhanced-scouting-files-viewer-next-arrow-container"
        >
          {showNextArrow && <Icon glyph={Icon.set.ARROW_RIGHT} testId="enhanced-scouting-files-viewer-next-arrow" />}
        </div>
      </div>
    }
  </div>
};
